// cspell:ignore Sommel firestore
// src/services/pipelineIntegration.js
/**
 * SommelAI Pipeline Integration
 *
 * This file demonstrates how to integrate the AI enhancement pipeline
 * with the existing SommelAI application, including Firestore integration
 * and UI component updates.
 */

import {
  enhanceBeverageData,
  enhanceBeverageBatch,
  ENHANCED_DATA_SCHEMA,
} from "./aiEnhancementPipeline.js";
import { db } from "../firebase-config.js";
import { doc, updateDoc, getDoc, setDoc } from "firebase/firestore";

/**
 * Enhance a single beverage and update Firestore
 * @param {string} beverageId - The Firestore document ID
 * @param {string} category - Beverage category (wine, beer, spirit, etc.)
 * @param {Object} options - Enhancement options
 */
export async function enhanceAndUpdateBeverage(
  beverageId,
  category,
  options = {}
) {
  try {
    console.log(`🚀 Starting enhancement for beverage ${beverageId}`);

    // 1. Fetch current beverage data from Firestore
    const beverageRef = doc(db, "drinks", beverageId);
    const beverageSnap = await getDoc(beverageRef);

    if (!beverageSnap.exists()) {
      throw new Error(`Beverage ${beverageId} not found in database`);
    }

    const beverageData = { id: beverageId, ...beverageSnap.data() };

    // 2. Run AI enhancement pipeline
    const enhancedBeverage = await enhanceBeverageData(
      beverageData,
      category,
      options
    );

    // 3. Update Firestore with enhanced data
    await updateDoc(beverageRef, {
      ...enhancedBeverage,
      lastEnhanced: new Date().toISOString(),
      enhancementVersion: "1.0",
    });

    console.log(`✅ Successfully enhanced and updated ${beverageId}`);
    return enhancedBeverage;
  } catch (error) {
    console.error("Enhancement and update failed:", error);
    throw error;
  }
}

/**
 * Enhance multiple beverages in batch
 * @param {Array} beverageIds - Array of Firestore document IDs
 * @param {Object} options - Batch processing options
 */
export async function enhanceAndUpdateBeverageBatch(beverageIds, options = {}) {
  try {
    console.log(
      `🚀 Starting batch enhancement for ${beverageIds.length} beverages`
    );

    // 1. Fetch all beverages from Firestore
    const beverages = [];
    for (const id of beverageIds) {
      const beverageRef = doc(db, "drinks", id);
      const beverageSnap = await getDoc(beverageRef);

      if (beverageSnap.exists()) {
        beverages.push({ id, ...beverageSnap.data() });
      } else {
        console.warn(`Beverage ${id} not found, skipping`);
      }
    }

    // 2. Run batch enhancement
    const enhancedBeverages = await enhanceBeverageBatch(beverages, {
      batchSize: options.batchSize || 3, // Smaller batches for API limits
      delay: options.delay || 2000, // 2 second delay between batches
      ...options,
    });

    // 3. Update all beverages in Firestore
    const updatePromises = enhancedBeverages.map(async (beverage) => {
      if (
        beverage.enhanced?.status === "enhanced" ||
        beverage.enhanced?.status === "partial"
      ) {
        const beverageRef = doc(db, "drinks", beverage.id);
        return updateDoc(beverageRef, {
          ...beverage,
          lastEnhanced: new Date().toISOString(),
          enhancementVersion: "1.0",
        });
      }
    });

    await Promise.all(updatePromises.filter(Boolean));

    console.log(
      `✅ Batch enhancement complete: ${enhancedBeverages.length} beverages processed`
    );
    return enhancedBeverages;
  } catch (error) {
    console.error("Batch enhancement failed:", error);
    throw error;
  }
}

/**
 * Get enhancement status for a beverage
 * @param {string} beverageId - The Firestore document ID
 */
export async function getBeverageEnhancementStatus(beverageId) {
  try {
    const beverageRef = doc(db, "drinks", beverageId);
    const beverageSnap = await getDoc(beverageRef);

    if (!beverageSnap.exists()) {
      return { exists: false };
    }

    const data = beverageSnap.data();
    return {
      exists: true,
      enhanced: data.enhanced || { status: "not_enhanced" },
      lastEnhanced: data.lastEnhanced,
      enhancementVersion: data.enhancementVersion,
    };
  } catch (error) {
    console.error("Failed to get enhancement status:", error);
    return { exists: false, error: error.message };
  }
}

/**
 * Initialize enhancement for restaurant's entire menu
 * @param {Object} options - Enhancement options
 */
export async function enhanceEntireMenu(options = {}) {
  try {
    console.log("🚀 Starting full menu enhancement...");

    // This would be a long-running process, so we should:
    // 1. Create a progress tracking document
    // 2. Process in small batches
    // 3. Provide progress updates
    // 4. Handle failures gracefully

    const progressRef = doc(
      db,
      "enhancement_progress",
      "menu_enhancement_" + Date.now()
    );
    await setDoc(progressRef, {
      status: "started",
      startTime: new Date().toISOString(),
      totalBeverages: 0,
      processedBeverages: 0,
      failedBeverages: 0,
      enhancedBeverages: 0,
    });

    // TODO: Implement full menu enhancement with progress tracking
    console.log(
      "Full menu enhancement is a long-running process that should be implemented with job queues"
    );

    return {
      message: "Full menu enhancement initiated",
      progressId: progressRef.id,
    };
  } catch (error) {
    console.error("Full menu enhancement failed:", error);
    throw error;
  }
}

/**
 * Demo function to test the enhancement pipeline
 */
export async function testEnhancementPipeline() {
  console.log("🧪 Testing SommelAI Enhancement Pipeline...");

  // Test wine data
  const testWine = {
    id: "test-wine-1",
    name: "Opus One",
    winery: "Opus One Winery",
    region: "Napa Valley",
    country: "United States",
    vintage: 2019,
    varietal: "Bordeaux Blend",
    price: 400,
    description: "Luxury Bordeaux-style blend from Napa Valley",
  };

  try {
    console.log("Testing wine enhancement...");
    const enhancedWine = await enhanceBeverageData(testWine, "wine");
    console.log("Wine enhancement result:", {
      status: enhancedWine.enhanced?.status,
      confidence: enhancedWine.enhanced?.confidenceScore,
      sources: enhancedWine.enhanced?.aiSources,
    });

    return {
      success: true,
      results: {
        wine: enhancedWine.enhanced,
      },
    };
  } catch (error) {
    console.error("Pipeline test failed:", error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Utility function to migrate existing drinks to enhanced schema
 */
export async function migrateDrinksToEnhancedSchema() {
  try {
    console.log("🔄 Starting migration to enhanced schema...");

    // This would scan all drinks and add the enhanced data structure
    // without running the AI enhancement yet

    // const drinksRef = collection(db, 'drinks');
    // TODO: Implement migration logic

    console.log("Migration would be implemented here");
    return { message: "Migration logic placeholder" };
  } catch (error) {
    console.error("Migration failed:", error);
    throw error;
  }
}

// Export all functions
const PipelineIntegration = {
  enhanceAndUpdateBeverage,
  enhanceAndUpdateBeverageBatch,
  getBeverageEnhancementStatus,
  enhanceEntireMenu,
  testEnhancementPipeline,
  migrateDrinksToEnhancedSchema,
  ENHANCED_DATA_SCHEMA,
};

export default PipelineIntegration;
